import React from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

export const formatDate = (value) => new Date(value).toLocaleDateString();

// Round numeric stats to at most 2 decimals (so 66.666666… renders as 66.67),
// while leaving integers and non-numeric values untouched.
export const formatNum = (value) =>
  typeof value === "number" && Number.isFinite(value)
    ? Math.round(value * 100) / 100
    : value;

// One game's scores plotted over time (date by date).
const GameTrend = ({ sessions }) => (
  <Line
    data={{
      labels: sessions.map((s) => formatDate(s.started_at)),
      datasets: [
        {
          label: "Score",
          data: sessions.map((s) => s.score),
          borderColor: "#4a7c59",
          backgroundColor: "#4a7c59",
          tension: 0.25,
        },
      ],
    }}
    options={{
      plugins: { legend: { display: false } },
      scales: { y: { beginAtZero: true } },
    }}
  />
);

// Shared, read-only progress display: summary cards + game-by-game + day-by-day.
// Used by the patient's own stats page and the public share-link page so both
// render identical numbers.
const StatsDisplay = ({ stats }) => {
  const byGame = stats.by_game || [];
  const byDate = stats.by_date || [];

  const cards = [
    { number: stats.sessions_played, label: "Games played" },
    { number: stats.completed, label: "Completed" },
    { number: formatNum(stats.best_score), label: "Best score" },
    { number: formatNum(stats.avg_score), label: "Average score" },
  ];

  return (
    <>
      <div className="ms-cards">
        {cards.map((c) => (
          <div className="ms-card" key={c.label}>
            <span className="ms-number">{c.number}</span>
            <span className="ms-label">{c.label}</span>
          </div>
        ))}
      </div>

      {byGame.length > 0 && (
        <section className="ms-section">
          <h2 className="ms-section-title">Game by game</h2>
          <div className="ms-games">
            {byGame.map((g) => (
              <div className="ms-game-card" key={g.game_id}>
                <div className="ms-game-head">
                  <span className="ms-game-name">{g.game}</span>
                  <span className="ms-game-last">
                    Last played {formatDate(g.last_played)}
                  </span>
                </div>

                <div className="ms-game-stats">
                  <div className="ms-stat">
                    <span className="ms-stat-num">{g.sessions_played}</span>
                    <span className="ms-stat-lbl">Played</span>
                  </div>
                  <div className="ms-stat">
                    <span className="ms-stat-num">{g.completed}</span>
                    <span className="ms-stat-lbl">Completed</span>
                  </div>
                  <div className="ms-stat">
                    <span className="ms-stat-num">{formatNum(g.best_score)}</span>
                    <span className="ms-stat-lbl">Best</span>
                  </div>
                  <div className="ms-stat">
                    <span className="ms-stat-num">{formatNum(g.avg_score)}</span>
                    <span className="ms-stat-lbl">Average</span>
                  </div>
                </div>

                {g.sessions.length > 1 && (
                  <div className="ms-game-chart">
                    <GameTrend sessions={g.sessions} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {byDate.length > 0 && (
        <section className="ms-section">
          <h2 className="ms-section-title">Day by day</h2>
          <div className="ms-table-wrap">
            <table className="ms-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Games played</th>
                  <th>Completed</th>
                  <th>Average score</th>
                </tr>
              </thead>
              <tbody>
                {byDate.map((d) => (
                  <tr key={d.date}>
                    <td>{formatDate(d.date)}</td>
                    <td>{d.sessions_played}</td>
                    <td>{d.completed}</td>
                    <td>{formatNum(d.avg_score)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
};

export default StatsDisplay;
